import React from 'react'

const variantStyles = {
  emergency: {
    card: 'bg-red-50/80 border-red-200 ring-red-100',
    iconWrap: 'bg-red-600 text-white',
    title: 'text-red-900',
    textLink: 'text-red-700 hover:text-red-800',
    number: 'text-red-700'
  },
  crisis: {
    card: 'bg-white border-primary-darkGreen/15 ring-primary-darkGreen/5',
    iconWrap: 'bg-primary-darkGreen text-white',
    title: 'text-primary-darkGreen',
    textLink: 'text-primary-darkGreen hover:text-primary-darkGreen/80',
    number: 'text-primary-darkGreen'
  },
  specialized: {
    card: 'bg-white border-gray-100 ring-gray-100/80',
    iconWrap: 'bg-primary-golden text-white',
    title: 'text-primary-darkGreen',
    textLink: 'text-primary-darkGreen hover:text-primary-darkGreen/80',
    number: 'text-primary-darkGreen'
  },
  florida: {
    card: 'bg-primary-offWhite border-primary-lightGray/60 ring-primary-lightGray/40',
    iconWrap: 'bg-primary-darkGreen text-white',
    title: 'text-primary-darkGreen',
    textLink: 'text-primary-darkGreen hover:text-primary-darkGreen/80',
    number: 'text-primary-darkGreen'
  },
  support: {
    card: 'bg-white border-gray-100 ring-gray-100/80 md:col-span-1',
    iconWrap: 'bg-primary-golden text-white',
    title: 'text-primary-darkGreen',
    textLink: 'text-primary-darkGreen hover:text-primary-darkGreen/80',
    number: 'text-primary-darkGreen'
  }
}

const textLinkClass = (linkColor) =>
  `text-sm font-semibold no-underline break-all transition-colors hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-darkGreen focus-visible:ring-offset-2 rounded-sm ${linkColor}`

/**
 * @param {object} props
 * @param {string} props.title
 * @param {string} [props.description]
 * @param {string} [props.number]
 * @param {string} [props.tel]
 * @param {string} [props.detail]
 * @param {string} [props.website]
 * @param {string} [props.email]
 * @param {'emergency'|'crisis'|'specialized'|'florida'|'support'} [props.variant]
 * @param {React.ReactNode} props.icon
 */
const ResourceCard = ({
  title,
  description,
  number,
  tel,
  detail,
  website,
  email,
  variant = 'crisis',
  icon
}) => {
  const styles = variantStyles[variant] ?? variantStyles.crisis
  const numberClass = `text-xl sm:text-2xl font-bold tracking-tight mb-3 wrap-break-word ${styles.number}`

  return (
    <article
      className={`flex flex-col h-full rounded-2xl border p-5 sm:p-6 shadow-sm ring-1 transition-shadow duration-200 hover:shadow-md ${styles.card}`}
    >
      <div className="flex items-start gap-3 mb-4">
        <div
          className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${styles.iconWrap}`}
          aria-hidden
        >
          {icon}
        </div>
        <h3 className={`text-base sm:text-lg font-bold leading-snug ${styles.title}`}>{title}</h3>
      </div>

      {number && (
        tel ? (
          <a href={`tel:${tel}`} className={`${numberClass} no-underline hover:opacity-80`}>
            {number}
          </a>
        ) : (
          <p className={numberClass}>{number}</p>
        )
      )}

      {detail && (
        <p className="text-sm text-gray-600 mb-3 leading-relaxed">{detail}</p>
      )}

      {description && (
        <p className="text-sm text-gray-600 leading-relaxed mb-4 grow">{description}</p>
      )}

      {(email || website) && (
        <div className="mt-auto flex flex-col gap-2 pt-2">
          {email && (
            <a href={`mailto:${email}`} className={textLinkClass(styles.textLink)}>
              {email}
            </a>
          )}
          {website && (
            <a
              href={`https://${website}`}
              target="_blank"
              rel="noopener noreferrer"
              className={textLinkClass(styles.textLink)}
            >
              {website}
            </a>
          )}
        </div>
      )}
    </article>
  )
}

export default ResourceCard
